import { useState, useRef, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { maplibregl } from '../../../shared/maplibre.js'
import 'maplibre-gl/dist/maplibre-gl.css'
import type { CheckoutType, CheckoutRequestData, CheckoutResponseData } from '../../../shared/api/cart.js'
import { checkout, CartApiError } from '../../../shared/api/cart.js'
import { MAP_TILES_URL, MAP_ATTRIBUTION, MAP_TILE_SIZE } from '../../../shared/map/config.js'

interface OrderAcceptance {
  pickupEnabled: boolean
  deliveryEnabled: boolean
}

interface Props {
  orgSlug: string
  locSlug: string
  currency: string
  cartItemsCount: number
  cartTotal: number
  orderAcceptance?: OrderAcceptance
  initialValues?: { name: string; phone: string; email: string; comment: string; type: string }
  onDraftChange?: (draft: { name: string; phone: string; email: string; comment: string; type: CheckoutType }) => void
  onSuccess: (result: CheckoutResponseData) => void
  onBack: () => void
  onPriceChanged: () => void
  onOrdersNotAccepting?: (details?: Record<string, unknown>) => Promise<string> | string
}

const DEFAULT_ORDER_ACCEPTANCE: OrderAcceptance = {
  pickupEnabled: true,
  deliveryEnabled: true,
}

export default function CheckoutForm({
  orgSlug, locSlug, currency, cartItemsCount, cartTotal,
  orderAcceptance = DEFAULT_ORDER_ACCEPTANCE, initialValues, onDraftChange,
  onSuccess, onBack, onPriceChanged, onOrdersNotAccepting,
}: Props) {
  const { t } = useTranslation()
  const idempotencyKeyRef = useRef<string>(crypto.randomUUID())

  const availableTypes: CheckoutType[] = [
    ...(orderAcceptance.pickupEnabled ? ['PICKUP' as const] : []),
    ...(orderAcceptance.deliveryEnabled ? ['DELIVERY' as const] : []),
  ]
  const requestedType = initialValues?.type
  const initialType: CheckoutType = availableTypes.includes(requestedType as CheckoutType)
    ? (requestedType as CheckoutType)
    : (availableTypes[0] ?? 'PICKUP')

  const [type, setType] = useState<CheckoutType>(initialType)
  const [guestName, setGuestName] = useState(initialValues?.name ?? '')
  const [guestPhone, setGuestPhone] = useState(initialValues?.phone ?? '')
  const [guestEmail, setGuestEmail] = useState(initialValues?.email ?? '')
  const [deliveryAddress, setDeliveryAddress] = useState('')
  const [deliveryCoords, setDeliveryCoords] = useState<{ lat: number; lng: number } | null>(null)
  const mapContainer = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const markerRef = useRef<maplibregl.Marker | null>(null)
  const [comment, setComment] = useState(initialValues?.comment ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  function updateDraft(next: Partial<{ type: CheckoutType; guestName: string; guestPhone: string; guestEmail: string; comment: string }>) {
    onDraftChange?.({
      name: next.guestName ?? guestName,
      phone: next.guestPhone ?? guestPhone,
      email: next.guestEmail ?? guestEmail,
      comment: next.comment ?? comment,
      type: next.type ?? type,
    })
  }

  function validate(): Record<string, string> {
    const errs: Record<string, string> = {}
    if (!guestName.trim()) errs.guestName = t('checkout.validation.nameRequired')
    if (!guestPhone.trim()) errs.guestPhone = t('checkout.validation.phoneRequired')
    if (guestEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail.trim())) {
      errs.guestEmail = t('checkout.validation.emailInvalid')
    }
    if (type === 'DELIVERY') {
      if (!deliveryAddress.trim()) errs.deliveryAddress = t('checkout.validation.addressRequired')
    }
    return errs
  }

  // Init map when DELIVERY is selected
  if (type === 'DELIVERY' && !mapRef.current && mapContainer.current) {
    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: { version: 8, sources: { osm: { type: 'raster', tiles: [MAP_TILES_URL], tileSize: MAP_TILE_SIZE, attribution: MAP_ATTRIBUTION } }, layers: [{ id: 'osm', type: 'raster', source: 'osm' }] },
      center: [37.6, 55.7],
      zoom: 12,
    })
    map.on('click', (e) => {
      setDeliveryCoords({ lat: e.lngLat.lat, lng: e.lngLat.lng })
      if (markerRef.current) markerRef.current.setLngLat(e.lngLat)
      else markerRef.current = new maplibregl.Marker().setLngLat(e.lngLat).addTo(map)
    })
    mapRef.current = map
  }
  if (type !== 'DELIVERY' && mapRef.current) {
    mapRef.current.remove()
    mapRef.current = null
    markerRef.current = null
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const errs = validate()
    setFieldErrors(errs)
    if (Object.keys(errs).length > 0) return

    setSubmitting(true)
    setSubmitError(null)

    try {
      const data: CheckoutRequestData = {
        type,
        guestName: guestName.trim(),
        guestPhone: guestPhone.trim(),
        guestEmail: guestEmail.trim() || undefined,
        deliveryAddress: type === 'DELIVERY' ? deliveryAddress.trim() : undefined,
        deliveryCoords: type === 'DELIVERY' && deliveryCoords ? deliveryCoords : undefined,
        comment: comment.trim() || undefined,
      }
      const result = await checkout(orgSlug, locSlug, idempotencyKeyRef.current, data)
      onSuccess(result)
    } catch (err: unknown) {
      if (err instanceof CartApiError) {
        switch (err.code) {
          case 'PRICE_CHANGED':
          case 'PRODUCT_UNAVAILABLE':
          case 'VARIANT_UNAVAILABLE':
          case 'MODIFIER_UNAVAILABLE':
            setSubmitError(t('checkout.errors.priceChanged'))
            onPriceChanged()
            return
          case 'CART_EMPTY':
            setSubmitError(t('checkout.errors.cartEmpty'))
            return
          case 'NOT_FOUND':
            setSubmitError(t('checkout.errors.cartNotFound'))
            return
          case 'VALIDATION_ERROR':
            setSubmitError(t('checkout.errors.validation'))
            return
          case 'DELIVERY_NOT_AVAILABLE':
          case 'DELIVERY_COORDS_REQUIRED':
          case 'INVALID_DELIVERY_COORDS':
          case 'MIN_ORDER_NOT_MET':
            setSubmitError(err.message || t('checkout.errors.deliveryNotAvailable'))
            return
          case 'ORDERS_NOT_ACCEPTING': {
            const message = await onOrdersNotAccepting?.(err.details)
            setSubmitError(message || t('checkout.errors.ordersNotAccepting'))
            return
          }
          case 'RATE_LIMITED':
            setSubmitError(t('checkout.errors.rateLimited'))
            return
          default:
            setSubmitError(err.message || t('checkout.errors.generic'))
            return
        }
      }
      setSubmitError(t('checkout.errors.networkError'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="storefront-checkout-form">
      <h3 className="storefront-checkout-title">{t('checkout.title')}</h3>

      <div className="storefront-checkout-summary">
        <p className="storefront-checkout-summary-text">
          {t('checkout.summary')}: {t('checkout.itemsCount', { count: cartItemsCount })} — <strong>{cartTotal} {currency}</strong>
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="storefront-checkout-type-selector">
          <span className="storefront-checkout-label">{t('checkout.orderType')}</span>
          <div className="storefront-checkout-type-options">
            {availableTypes.map(orderType => (
              <button
                key={orderType}
                type="button"
                className={`storefront-checkout-type-btn ${type === orderType ? 'active' : ''}`}
                onClick={() => { setType(orderType); updateDraft({ type: orderType }) }}
              >
                {t(`checkout.orderTypes.${orderType.toLowerCase()}`)}
              </button>
            ))}
          </div>
          {availableTypes.length === 0 && (
            <p className="storefront-checkout-field-error">{t('checkout.noOrderTypes')}</p>
          )}
        </div>

        <div className="storefront-checkout-fields">
          <div className="storefront-checkout-field">
            <label className="storefront-checkout-label">{t('checkout.name')} *</label>
            <input
              className={`storefront-checkout-input ${fieldErrors.guestName ? 'error' : ''}`}
              value={guestName}
              onChange={e => { setGuestName(e.target.value); updateDraft({ guestName: e.target.value }) }}
              placeholder={t('checkout.namePlaceholder')}
              autoComplete="name"
            />
            {fieldErrors.guestName && <p className="storefront-checkout-field-error">{fieldErrors.guestName}</p>}
          </div>

          <div className="storefront-checkout-field">
            <label className="storefront-checkout-label">{t('checkout.phone')} *</label>
            <input
              className={`storefront-checkout-input ${fieldErrors.guestPhone ? 'error' : ''}`}
              value={guestPhone}
              onChange={e => { setGuestPhone(e.target.value); updateDraft({ guestPhone: e.target.value }) }}
              placeholder={t('checkout.phonePlaceholder')}
              type="tel"
              autoComplete="tel"
            />
            {fieldErrors.guestPhone && <p className="storefront-checkout-field-error">{fieldErrors.guestPhone}</p>}
          </div>

          <div className="storefront-checkout-field">
            <label className="storefront-checkout-label">{t('checkout.email')}</label>
            <input
              className={`storefront-checkout-input ${fieldErrors.guestEmail ? 'error' : ''}`}
              value={guestEmail}
              onChange={e => { setGuestEmail(e.target.value); updateDraft({ guestEmail: e.target.value }) }}
              placeholder={t('checkout.emailPlaceholder')}
              type="email"
              autoComplete="email"
            />
            {fieldErrors.guestEmail && <p className="storefront-checkout-field-error">{fieldErrors.guestEmail}</p>}
          </div>

          {type === 'DELIVERY' && (
            <>
              <div className="storefront-checkout-field">
                <label className="storefront-checkout-label">{t('checkout.deliveryAddress')} *</label>
                <input
                  className={`storefront-checkout-input ${fieldErrors.deliveryAddress ? 'error' : ''}`}
                  value={deliveryAddress}
                  onChange={e => setDeliveryAddress(e.target.value)}
                  placeholder={t('checkout.deliveryAddressPlaceholder')}
                  autoComplete="street-address"
                />
                {fieldErrors.deliveryAddress && <p className="storefront-checkout-field-error">{fieldErrors.deliveryAddress}</p>}
              </div>
              <div className="storefront-checkout-field">
                <label className="storefront-checkout-label">{t('checkout.deliveryOnMapOptional')}</label>
                <div
                  ref={mapContainer}
                  style={{ height: 200, borderRadius: 8, overflow: 'hidden', cursor: 'crosshair' }}
                  onClick={e => {
                    if (!mapRef.current || !mapContainer.current) return
                    const rect = mapContainer.current.getBoundingClientRect()
                    const lngLat = mapRef.current.unproject([e.clientX - rect.left, e.clientY - rect.top])
                    setDeliveryCoords({ lat: lngLat.lat, lng: lngLat.lng })
                    if (markerRef.current) markerRef.current.setLngLat(lngLat)
                    else markerRef.current = new maplibregl.Marker().setLngLat(lngLat).addTo(mapRef.current)
                  }}
                />
                {deliveryCoords && <p className="storefront-checkout-field-hint" style={{ fontSize: 12, color: '#666' }}>
                  {t('checkout.coordsSelected')}: {deliveryCoords.lat.toFixed(5)}, {deliveryCoords.lng.toFixed(5)}
                </p>}
              </div>
            </>
          )}

          <div className="storefront-checkout-field">
            <label className="storefront-checkout-label">{t('checkout.comment')}</label>
            <textarea
              className="storefront-checkout-input"
              value={comment}
              onChange={e => { setComment(e.target.value); updateDraft({ comment: e.target.value }) }}
              placeholder={t('checkout.commentPlaceholder')}
              rows={2}
            />
          </div>
        </div>

        {submitError && <p className="storefront-checkout-submit-error">{submitError}</p>}

        <div className="storefront-checkout-actions">
          <button type="button" className="btn btn-ghost" onClick={onBack} disabled={submitting}>
            {t('checkout.backToCart')}
          </button>
          <button
            type="submit"
            className="storefront-checkout-submit"
            disabled={submitting || availableTypes.length === 0}
          >
            {submitting ? t('checkout.submitting') : t('checkout.submit')}
          </button>
        </div>
      </form>
    </div>
  )
}